import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldType } from '../../../../core/fields/types';
import type { FieldManifest, FieldOption, FieldValue } from '../../../../core/fields/types';
import { MultiSelectRenderer } from './MultiSelectRenderer';

export type PaletteMultiSelectOption = FieldOption;

interface PaletteMultiSelectProps {
  id: string;
  value: string[];
  options: PaletteMultiSelectOption[];
  placeholder?: string;
  allowCustomOptions?: boolean;
  readonly?: boolean;
  helpText?: string;
  label?: string;
  readOnlyValues?: string[];
  readOnlyTooltip?: string;
  onCommit: (values: string[]) => void;
}

const normalizeValues = (values: string[] | undefined) => (Array.isArray(values) ? values : []);
const arraysEqual = (a: string[], b: string[]) => a.length === b.length && a.every((val, idx) => val === b[idx]);

export const PaletteMultiSelect: React.FC<PaletteMultiSelectProps> = ({
  id,
  value,
  options,
  placeholder,
  allowCustomOptions,
  readonly,
  helpText,
  label,
  readOnlyValues,
  readOnlyTooltip,
  onCommit,
}) => {
  const readOnlySet = useMemo(() => new Set((readOnlyValues ?? []).map(String)), [readOnlyValues]);
  const normalizedValue = useMemo(() => {
    const base = normalizeValues(value);
    if (readOnlySet.size === 0) return base;
    const next = new Set(base);
    readOnlySet.forEach(val => next.add(val));
    return Array.from(next);
  }, [value, readOnlySet]);
  const [displayValue, setDisplayValue] = useState<string[]>(normalizedValue);
  const committedValuesRef = useRef<string[]>(normalizedValue);
  const pendingValuesRef = useRef<string[]>(normalizedValue);

  useEffect(() => {
    if (!arraysEqual(normalizedValue, pendingValuesRef.current)) {
      pendingValuesRef.current = normalizedValue;
      setDisplayValue(normalizedValue);
    }
    if (!arraysEqual(normalizedValue, committedValuesRef.current)) {
      committedValuesRef.current = normalizedValue;
    }
  }, [normalizedValue]);

  const fieldManifest: FieldManifest = useMemo(() => ({
    id,
    label: label ?? id,
    type: FieldType.MULTI_SELECT,
    tags: [],
    placeholder,
    allowCustomOptions,
    readonly,
    helpText,
    options,
  }), [allowCustomOptions, helpText, id, label, options, placeholder, readonly]);

  const applyPendingChanges = useCallback((pending: string[]) => {
    const committed = committedValuesRef.current;
    if (arraysEqual(pending, committed)) {
      return;
    }

    const explicitValues = pending.filter(v => !readOnlySet.has(v));
    committedValuesRef.current = pending;
    onCommit(explicitValues);
  }, [onCommit, readOnlySet]);

  const handleChange = (next: FieldValue) => {
    const rawNextValues = normalizeValues(Array.isArray(next) ? next.map(String) : []);
    const nextValues = readOnlySet.size === 0
      ? rawNextValues
      : Array.from(new Set([...rawNextValues, ...Array.from(readOnlySet)]));

    pendingValuesRef.current = nextValues;
    setDisplayValue(nextValues);
  };

  const handleCommit = useCallback(() => {
    applyPendingChanges(pendingValuesRef.current);
  }, [applyPendingChanges]);

  useEffect(() => () => {
    handleCommit();
  }, [handleCommit]);

  return (
    <MultiSelectRenderer
      field={fieldManifest}
      value={displayValue}
      onChange={handleChange}
      readOnlyValues={Array.from(readOnlySet)}
      readOnlyTooltip={readOnlyTooltip}
      closeOnCustomCreate
      onOpenChange={(open) => {
        if (!open) {
          handleCommit();
        }
      }}
    />
  );
};
